import { feedbackForCopyOutcome, feedbackForNativeShareOutcome, type ShareModalFeedback } from "./shareChallengeModalContent";
import type { CopyLinkOutcome, NativeShareOutcome } from "./shareFlow";
import type { ShareContext } from "@/analytics/events";

export type ShareChallengeModalState =
  | { phase: "closed" }
  | {
      phase: "open";
      creatorDisplayName: string;
      targetDailyPoints: number;
      url: string;
      canNativeShare: boolean;
      busy: boolean;
      feedback: ShareModalFeedback;
    };

export interface ShareChallengeControllerDeps {
  performNativeShare: (context: ShareContext, dailyPoints: number, url: string) => Promise<NativeShareOutcome>;
  /** Deliberately takes only `url`, never `dailyPoints`/share text - COPY LINK must write ONLY the bare canonical URL to the clipboard, never the share message. See `performCopyLink`'s doc in `shareFlow.ts`. */
  performCopyLink: (context: ShareContext, url: string) => Promise<CopyLinkOutcome>;
  canUseNativeShare: () => boolean;
  onChange: (state: ShareChallengeModalState) => void;
}

/**
 * The pure, framework-free state machine behind the "CHALLENGE A FRIEND"
 * modal - deliberately holds no Phaser objects at all, so every behavior
 * the round-5 spec calls out (opening never invokes `navigator.share`,
 * SHARE/COPY LINK are only ever invoked by an explicit call, a
 * cancellation/failure returns to the still-open modal rather than
 * closing or auto-falling-through) is directly unit-testable, the same
 * "extract a pure decision/state class out of the Scene" pattern this
 * codebase already uses for `resolveScoreChallengeLookup`/`RunFinishedGuard`.
 *
 * `ShareChallengeModal.ts` (the Phaser view) owns exactly one instance,
 * wired to the real `performNativeShare`/`performCopyLink`/
 * `canUseNativeShare` from `shareFlow.ts` (or their QA-fixture,
 * no-tracking counterparts) and re-renders whenever `onChange` fires.
 */
export class ShareChallengeController {
  private state: ShareChallengeModalState = { phase: "closed" };

  constructor(
    private readonly context: ShareContext,
    private readonly deps: ShareChallengeControllerDeps,
  ) {}

  getState(): ShareChallengeModalState {
    return this.state;
  }

  /** Opens the modal with a freshly created/retrieved challenge - never itself touches the network, the clipboard, or `navigator.share()`. */
  open(creatorDisplayName: string, targetDailyPoints: number, url: string): void {
    this.state = {
      phase: "open",
      creatorDisplayName,
      targetDailyPoints,
      url,
      canNativeShare: this.deps.canUseNativeShare(),
      busy: false,
      feedback: { kind: "none" },
    };
    this.emit();
  }

  close(): void {
    if (this.state.phase === "closed") return;
    this.state = { phase: "closed" };
    this.emit();
  }

  isOpen(): boolean {
    return this.state.phase === "open";
  }

  /** Only ever called by an explicit SHARE tap - see `performNativeShare`'s doc for why `score_share_requested` lives there, not here or on `open()`. */
  async share(): Promise<void> {
    if (this.state.phase !== "open" || this.state.busy) return;
    const { targetDailyPoints, url } = this.state;
    this.patch({ busy: true, feedback: { kind: "none" } });

    const outcome = await this.deps.performNativeShare(this.context, targetDailyPoints, url);
    // The modal may have been closed while the native share sheet was
    // still open (e.g. the player backed out to the menu) - a late
    // result must never reopen it or resurrect stale feedback.
    if (this.state.phase !== "open") return;
    this.patch({ busy: false, feedback: feedbackForNativeShareOutcome(outcome, url) });
  }

  /** Only ever called by an explicit COPY LINK tap. Passes only `url` down - never the target/score - so the clipboard write can never end up carrying anything but the bare canonical link. */
  async copy(): Promise<void> {
    if (this.state.phase !== "open" || this.state.busy) return;
    const { url } = this.state;
    this.patch({ busy: true, feedback: { kind: "none" } });

    const outcome = await this.deps.performCopyLink(this.context, url);
    if (this.state.phase !== "open") return;
    this.patch({ busy: false, feedback: feedbackForCopyOutcome(outcome, url) });
  }

  private patch(partial: { busy: boolean; feedback: ShareModalFeedback }): void {
    if (this.state.phase !== "open") return;
    this.state = { ...this.state, ...partial };
    this.emit();
  }

  private emit(): void {
    this.deps.onChange(this.state);
  }
}
